import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers";
import { _ACTIONS, _COLORS } from "../../utils/_constants";
import { UserContext } from "../contexts/UserContext";
import { btn } from "../../utils/_style_constants";
import {
  getDefaultPeriodeRange,
  getQueryParamsFromFormData,
  toApiDate,
} from "../../utils/formHelper";
import { RequestHelper } from "../../utils/requestHelper";

/**
 * @desc The periodeOpt array contains the options for the periode select.
 * Each option has a value and a text to display.
 * @type {Array}
 */
export const periodeOpt = [
  { value: "", text: "Aucune période" },
  { value: "currentYear", text: "Cette année" },
  { value: "lastYear", text: "L'année dernière" },
  { value: "currentMonth", text: "Ce mois-ci" },
  { value: "lastMonth", text: "Le mois dernier" },
  { value: "last3Month", text: "Les 3 derniers mois" },
  { value: "last6Month", text: "Les 6 derniers mois" },
];

export function SignRequestFilterForm(props) {
  const { control, handleSubmit, register, setValue } = useForm();
  const [searchNumeroRequest, setSearchNumeroRequest] = useState("");
  const [statutSelected, setStatutSelected] = useState("");
  const [periodeSelected, setPeriodeSelected] = useState("last6Month");
  const [dateDebValue, setDateDebValue] = useState(
    () => getDefaultPeriodeRange().debut,
  );
  const [dateFinValue, setDateFinValue] = useState(
    () => getDefaultPeriodeRange().fin,
  );
  const [disabled, setDisabled] = useState(true);
  const { user } = useContext(UserContext);
  const formRef = React.useRef(null);
  const requestHelper = new RequestHelper();

  // register() renvoie son propre onChange : on le récupère pour le chaîner
  // avec la mise à jour du state local, sinon l'un écrase l'autre selon
  // l'ordre d'étalement des props et le champ devient inutilisable.
  const { onChange: onRequestIdChange, ...requestIdField } = register(
    "requestId",
    { required: false },
  );
  const { onChange: onRequestStatusChange, ...requestStatusField } = register(
    "requestStatus",
    { required: false },
  );

  const statuts = [
    { value: 0, label: "En attente" },
    { value: 3, label: "En Cours" },
    { value: 4, label: "Terminé" },
    { value: 5, label: "Requète annulée par un signataire" },
    { value: 6, label: "Requète annulée par l'application" },
    { value: 7, label: "Requète expirée" },
    { value: 8, label: "Requète en erreur" },
  ];

  const handleDateDebChange = (date) => {
    setDateDebValue(date);
    setValue("createdAtAF", toApiDate(date, "start"));
  };

  const handleDateFinChange = (date) => {
    setDateFinValue(date);
    setValue("createdAtBF", toApiDate(date, "end"));
  };

  /**
   * Handles the change of the periode select.
   *
   * @param {*} e
   */
  const handlePeriodeChange = (e) => {
    setPeriodeSelected(e.target.value);
    setDisabled(true);
    let debVal;
    let finVal;
    switch (e.target.value) {
      case "currentYear":
        debVal = moment().startOf("year");
        finVal = moment().endOf("year");
        break;
      case "lastYear":
        debVal = moment().subtract(1, "year").startOf("year");
        finVal = moment().subtract(1, "year").endOf("year");
        break;
      case "currentMonth":
        debVal = moment().startOf("month");
        finVal = moment().endOf("month");
        break;
      case "lastMonth":
        debVal = moment().subtract(1, "month").startOf("month");
        finVal = moment().subtract(1, "month").endOf("month");
        break;
      case "last3Month":
        debVal = moment().subtract(3, "month").startOf("month");
        finVal = moment().endOf("month");
        break;
      case "last6Month":
        debVal = getDefaultPeriodeRange().debut;
        finVal = getDefaultPeriodeRange().fin;
        break;
      default:
        setDisabled(false);
    }
    if (e.target.value === "") {
      debVal = null;
      finVal = null;
      setDateDebValue(debVal);
      setDateFinValue(finVal);
      setValue("createdAtAF", "");
      setValue("createdAtBF", "");

      return;
    }
    setDateDebValue(debVal);
    setDateFinValue(finVal);
    setValue("createdAtAF", toApiDate(debVal, "start"));
    setValue("createdAtBF", toApiDate(finVal, "end"));
  };

  const onSearch = (formData) => {
    // Les paramètres sont passés à axios, qui se charge de les encoder :
    // aucune concaténation manuelle dans l'URL (un "+" ou un "&" présent dans
    // une valeur corromprait la query string).
    const params = getQueryParamsFromFormData(formData);
    params["groups[]"] = "sr:client";

    // restriction pour les utilisateurs
    const listeAgenceUser = user?.agences;

    if (user.profil !== "SADMIN" && listeAgenceUser) {
      params["mandat.agence[]"] = listeAgenceUser.map((agence) => agence.id);
    }

    requestHelper
      .doRequest(_ACTIONS.GET, "/api/sign_requests", params)
      .then((response) => {
        if (response.status === 200)
          props.setRowsSignRequest(response.data["hydra:member"]);
      });
  };

  /**
   * Renseigne la période par défaut dans le formulaire dès le montage, avant
   * le submit déclenché plus bas : la première requête est ainsi filtrée sur
   * les 6 derniers mois.
   */
  useEffect(() => {
    setValue("createdAtAF", toApiDate(dateDebValue, "start"));
    setValue("createdAtBF", toApiDate(dateFinValue, "end"));
  }, []);

  useEffect(() => {
    formRef.current.dispatchEvent(
      new Event("submit", { cancelable: true, bubbles: true }),
    );
  }, [props.refresh]);

  return (
    <>
      <form
        ref={formRef}
        action=""
        onSubmit={handleSubmit(onSearch)}
        style={{ width: "100%" }}
      >
        <Grid rowSpacing={1} container>
          <Grid item xl={3}>
            <TextField
              sx={{ width: "90%" }}
              size="small"
              label="N° Requête"
              {...requestIdField}
              value={searchNumeroRequest}
              onChange={(e) => {
                setSearchNumeroRequest(e.target.value);
                onRequestIdChange(e);
              }}
            ></TextField>
          </Grid>
          <Grid item xl={3}>
            <FormControl sx={{ width: "90%" }}>
              <InputLabel size="small">Statut</InputLabel>
              <Select
                label="requestStatus"
                size="small"
                {...requestStatusField}
                id="requestStatus"
                value={statutSelected}
                onChange={(e) => {
                  setStatutSelected(e.target.value);
                  onRequestStatusChange(e);
                }}
                input={<OutlinedInput label="Name" />}
              >
                <MenuItem value="">Tous</MenuItem>
                {statuts.map((statut, i) => (
                  <MenuItem key={i} value={statut.value}>
                    {statut.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xl={3}></Grid>
          <Grid item xl={3}></Grid>
          <Grid item xl={3}>
            <FormControl sx={{ width: "90%" }}>
              <InputLabel size="small">Période</InputLabel>
              <Select
                label="Période"
                size="small"
                id="periode"
                value={periodeSelected}
                input={<OutlinedInput label="Name" />}
                onChange={handlePeriodeChange}
                MenuProps={""}
              >
                {periodeOpt.map((periode, i) => (
                  <MenuItem key={i} value={periode.value}>
                    {periode.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xl={3}>
            <Controller
              name="createdAtAF"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <DatePicker
                  sx={{ width: "90%" }}
                  label="Date de début"
                  size="small"
                  format="DD/MM/YYYY"
                  value={dateDebValue}
                  disabled={disabled}
                  onChange={handleDateDebChange}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xl={3}>
            <Controller
              name="createdAtBF"
              control={control}
              defaultValue={null}
              value={dateFinValue}
              render={({ field }) => (
                <DatePicker
                  sx={{ width: "90%" }}
                  label="Date de Fin"
                  size="small"
                  format="DD/MM/YYYY"
                  value={dateFinValue}
                  disabled={disabled}
                  onChange={handleDateFinChange}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xl={1}></Grid>
          <Grid item xl={2}>
            <Button
              sx={{
                ...btn(_COLORS.primary, _COLORS.primaryLight),
                width: "85%",
              }}
              variant="Rechercher"
              type="submit"
            >
              Rechercher
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
